import {
  type Diagnostic,
  type RoutineView,
  type ValidationContext,
  type ValidationRule,
} from '../types';

import type { WarningFinding } from './errors';

export function diag(error: {
  code: string;
  message: string;
  stepId: string;
  fieldId?: string;
  severity?: 'error' | 'warning';
  tags?: string[];
  sourceRule: string;
}): Diagnostic {
  return {
    code: error.code,
    message: error.message,
    severity: error.severity ?? 'error',
    stepId: error.stepId,
    fieldId: error.fieldId,
    tags: error.tags,
    sourceRule: error.sourceRule,
  };
}

/**
 * Hands non-blocking findings to the rule's collector. Ignores `undefined` and
 * empty lists, so a check's return value passes straight in whether it produces
 * one finding, several, or none.
 */
export type Warn = (
  findings: WarningFinding | undefined | Array<WarningFinding | undefined>,
) => void;

/**
 * The JavaScript-expression deprecation asks the author to migrate to Python,
 * so it is only worth raising where Python expressions exist. Keyed on
 * `deprecationKind` rather than on the diagnostic code, so deprecating
 * something else later is not silenced by this flag too.
 */
function isSilencedDeprecation(
  finding: WarningFinding,
  ctx: ValidationContext,
): boolean {
  return (
    finding.deprecationKind === 'javaScriptExpressions' &&
    !ctx.featureFlags?.pythonExpressions
  );
}

export function createStepRule(opts: {
  id: string;
  stepKinds: string[];
  run: (args: {
    routine: RoutineView;
    ctx: ValidationContext;
    stepId: string;
    warn: Warn;
  }) => Diagnostic[];
}): ValidationRule {
  return {
    id: opts.id,
    appliesTo: 'step',
    stepKinds: opts.stepKinds,
    run: ({ routine, ctx, stepId }) => {
      if (!stepId) return [];

      // Collected rather than thrown, so a non-blocking finding can never end
      // the checks that follow it — including the runner-schema cross-check,
      // which runs outside the rule's own sequence and so no ordering within
      // that sequence can protect.
      const warnings: Diagnostic[] = [];

      const warn: Warn = (findings) => {
        if (!findings) return;

        const list = Array.isArray(findings) ? findings : [findings];

        for (const finding of list) {
          if (!finding || isSilencedDeprecation(finding, ctx)) continue;

          warnings.push(
            diag({
              code: `ui.${finding.kind}`,
              message: finding.message,
              fieldId: finding.fieldId,
              stepId,
              sourceRule: opts.id,
              severity: 'warning',
              tags: finding.deprecationKind
                ? [`deprecationKind:${finding.deprecationKind}`]
                : undefined,
            }),
          );
        }
      };

      return [...opts.run({ routine, ctx, stepId, warn }), ...warnings];
    },
  };
}

/**
 * Structural view of `ActionRequired` as read off a caught value, so a throw
 * from outside this library still converts. Deliberately looser than the real
 * type — `kind` is a bare string here — but it must carry every field the
 * conversion reads.
 */
type ActionRequiredShape = {
  kind: string;
  message?: string;
  fieldId?: string;
};

/**
 * The `ActionRequired` a thrown error carries, if it carries one.
 *
 * Duck-typed on purpose. Two `ActionRequiredError` classes reach this: the
 * rules' own (`util/errors`) and the one step converters throw
 * (`@sb/remote-control/types`). They are separate classes, so `instanceof`
 * recognises only one of them.
 */
export function readActionRequired(
  err: unknown,
): ActionRequiredShape | undefined {
  return (err as { actionRequired?: ActionRequiredShape } | undefined)
    ?.actionRequired;
}

/**
 * Convert an ActionRequired shape into a Diagnostic. Always blocking: an
 * `ActionRequired` is what a rule throws, and the throw channel carries only
 * findings the author has to act on. Non-blocking findings are `WarningFinding`s
 * handed to `warn`, which tags and tiers them itself.
 */
export function actionRequiredToDiagnostic(
  ar: ActionRequiredShape,
  stepId: string,
  sourceRule: string,
): Diagnostic {
  return diag({
    code: `ui.${ar.kind}`,
    message: ar.message ?? 'Invalid configuration',
    fieldId: ar.fieldId,
    stepId,
    sourceRule,
    severity: 'error',
  });
}

/**
 * Convert any caught error into Diagnostics. Recognises ActionRequiredError
 * shapes (kind, message, fieldId), bare Error, and unknown. Single source of
 * truth for error→diagnostic conversion across both class-based
 * (CanonicalStepRuleBase) and function-style rules.
 */
export function errorToDiagnostics(
  err: unknown,
  stepId: string,
  sourceRule: string,
): Diagnostic[] {
  const ar = readActionRequired(err);

  if (ar) {
    return [actionRequiredToDiagnostic(ar, stepId, sourceRule)];
  }

  if (err instanceof Error) {
    return [
      diag({
        code: 'ui.invalidConfiguration',
        message: err.message,
        stepId,
        sourceRule,
      }),
    ];
  }

  return [
    diag({
      code: 'ui.invalidConfiguration',
      message: `Configuration is not valid (${String(err)})`,
      stepId,
      sourceRule,
    }),
  ];
}

/**
 * Catch an ActionRequiredError thrown by legacy validators (e.g.
 * validateConditional) and convert it to a Diagnostic[]. Returns an empty
 * array for non-AR / non-Error values — this swallow-and-continue semantic
 * is intentional: callers run multiple checks in sequence and don't want one
 * unrelated throw to mask the others.
 *
 * Conversion logic delegates to `errorToDiagnostics` so the format stays
 * in sync with the rule-runner's catch path in `rules/steps/base.ts`.
 */
export function catchActionRequiredToDiagnostic(
  err: unknown,
  stepId: string,
  sourceRule: string,
): Diagnostic[] {
  if (!readActionRequired(err) && !(err instanceof Error)) return [];

  return errorToDiagnostics(err, stepId, sourceRule);
}
